import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { contractingSettingsSchema, DEFAULT_CONTRACTING_SETTINGS } from './contracting.js'
import { HEALTH_LEVELS, SIGNAL_CODES, type StoredProjectHealth } from './project-insights.js'
import { DEFAULT_TM_EXPENSE_MARKUP_PCT, DEFAULT_TM_GROUP_BY, TM_GROUPINGS } from './projects-billing.js'
import {
  DEFAULT_BUDGET_CRITICAL_PCT,
  DEFAULT_BUDGET_WARN_PCT,
  DEFAULT_PROJECT_CATEGORY_DEFAULTS,
  projectCategoryDefaultsSchema,
} from './projects-costs.js'
import {
  DEFAULT_PROJECT_TIME_SETTINGS,
  projectTimeSettingsSchema,
  type RateRoleRef,
} from './projects-time.js'

/**
 * Projects (ADR-039; docs/refactor/PROJECTS.md and projects/01–06): one switchable module whose projects
 * collect their own numbers from the documents the company already records. Release P1 (foundation and
 * cockpit) defines the project, its members, its plan (WBS), notes and lifecycle; later releases add
 * budgets and costs (P2), billing and insights (P3), contracting (P4–P5), time (P6), history (P7) and the
 * AI assistant (P8). Money travels as decimal strings; percentages as decimal strings 0–100.
 */

// ── shared pieces ────────────────────────────────────────────────────────────

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
/** A percentage 0–100 with up to two decimals, as a string ("61.5"). */
export const percentSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Invalid percentage')
  .refine((v) => Number(v) <= 100, 'At most 100')
const reasonSchema = requiredText(1000, 'Give a reason')

// ── vocabularies ─────────────────────────────────────────────────────────────

/**
 * PLANNING → ACTIVE ⇄ ON_HOLD → COMPLETED → CLOSED; CANCELLED from PLANNING, ACTIVE or ON_HOLD.
 * COMPLETED: the work is done (the defects period may run). CLOSED: financially closed — no new
 * documents are tagged to it and no work is recorded; only `reopen` changes it.
 */
export const PROJECT_STATUSES = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CLOSED', 'CANCELLED'] as const
export const projectStatusSchema = z.enum(PROJECT_STATUSES)
export type ProjectStatus = z.infer<typeof projectStatusSchema>

/** Statuses in which documents may be tagged to the project and work recorded on it. */
export const PROJECT_OPEN_STATUSES: readonly ProjectStatus[] = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED']

/** CUSTOMER: work for a customer, billable. INTERNAL: the company's own (capex, roll-out), cost only. */
export const PROJECT_KINDS = ['CUSTOMER', 'INTERNAL'] as const
export const projectKindSchema = z.enum(PROJECT_KINDS)
export type ProjectKind = z.infer<typeof projectKindSchema>

export const PROJECT_BILLING_METHODS = [
  'FIXED_PRICE',
  'TIME_AND_MATERIALS',
  'PROGRESS_CLAIMS',
  'NON_BILLABLE',
] as const
export const projectBillingMethodSchema = z.enum(PROJECT_BILLING_METHODS)
export type ProjectBillingMethod = z.infer<typeof projectBillingMethodSchema>

export const PROJECT_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const
export const projectPrioritySchema = z.enum(PROJECT_PRIORITIES)
export type ProjectPriority = z.infer<typeof projectPrioritySchema>

/** A MANAGER member manages that project (as `projects.manage` would, on it alone). */
export const PROJECT_MEMBER_ROLES = ['MANAGER', 'MEMBER'] as const
export const projectMemberRoleSchema = z.enum(PROJECT_MEMBER_ROLES)
export type ProjectMemberRole = z.infer<typeof projectMemberRoleSchema>

/** The plan is one tree: phases contain work packages and milestones; a milestone is always a leaf. */
export const WBS_NODE_KINDS = ['PHASE', 'WORK_PACKAGE', 'MILESTONE'] as const
export const wbsNodeKindSchema = z.enum(WBS_NODE_KINDS)
export type WbsNodeKind = z.infer<typeof wbsNodeKindSchema>
/** At most this many nodes per project (projects/01). */
export const WBS_NODE_LIMIT = 2000

/**
 * How a project's progress is measured (projects/05 §A2). MANUAL: dated updates per node, rolled up by
 * weight. TASKS: estimate-weighted share of completed project tasks. MILESTONES: achieved milestones by
 * weight. COST_TO_COST: actual cost ÷ the approved budget, both without `progressExcluded` categories,
 * per node on its subtree and capped at 100 (needs the `budgets` function). BOQ arrives with contracting (P4).
 */
export const PROJECT_PROGRESS_METHODS = ['MANUAL', 'TASKS', 'MILESTONES', 'COST_TO_COST', 'BOQ'] as const
export const projectProgressMethodSchema = z.enum(PROJECT_PROGRESS_METHODS)
export type ProjectProgressMethod = z.infer<typeof projectProgressMethodSchema>
/** The methods that work today (the list grows with the releases). */
export const PROJECT_PROGRESS_METHODS_AVAILABLE: readonly ProjectProgressMethod[] = [
  'MANUAL',
  'TASKS',
  'MILESTONES',
  'COST_TO_COST',
]

// ── lifecycle ────────────────────────────────────────────────────────────────

export const PROJECT_COMMANDS = [
  'activate',
  'hold',
  'resume',
  'complete',
  'close',
  'reopen',
  'cancel',
] as const
export type ProjectCommand = (typeof PROJECT_COMMANDS)[number]

/** The one move table (projects/01 §Lifecycle). The API refuses any other move with PROJECT_ILLEGAL_MOVE. */
export const PROJECT_MOVES: Record<ProjectCommand, { from: readonly ProjectStatus[]; to: ProjectStatus }> = {
  activate: { from: ['PLANNING'], to: 'ACTIVE' },
  hold: { from: ['ACTIVE'], to: 'ON_HOLD' },
  resume: { from: ['ON_HOLD'], to: 'ACTIVE' },
  complete: { from: ['ACTIVE'], to: 'COMPLETED' },
  close: { from: ['COMPLETED'], to: 'CLOSED' },
  reopen: { from: ['COMPLETED', 'CLOSED'], to: 'ACTIVE' },
  cancel: { from: ['PLANNING', 'ACTIVE', 'ON_HOLD'], to: 'CANCELLED' },
}

export function projectMoveAllowed(command: ProjectCommand, status: ProjectStatus): boolean {
  return PROJECT_MOVES[command].from.includes(status)
}

export const projectVersionSchema = z.object({ version: versionSchema })
/** Hold, reopen and cancel record why. */
export const projectReasonCommandSchema = z.object({ version: versionSchema, reason: reasonSchema })
export type ProjectReasonCommand = z.input<typeof projectReasonCommandSchema>
/** Activate may confirm the actual start (default: today in Muscat). */
export const projectActivateSchema = z.object({ version: versionSchema, actualStart: nullableDate })
export type ProjectActivateInput = z.input<typeof projectActivateSchema>
/** Complete may confirm the actual finish (default: today in Muscat). */
export const projectCompleteSchema = z.object({ version: versionSchema, actualFinish: nullableDate })
export type ProjectCompleteInput = z.input<typeof projectCompleteSchema>

// ── functions (feature switches) ─────────────────────────────────────────────

/**
 * Every function of the module is a switch: in the company's settings (group `projects`) and in each
 * project type. A function is effective on a project when the company has it on and the project's own
 * copy (taken from its type at creation) has it on (ADR-039).
 */
export const PROJECT_FEATURES = [
  'wbs',
  'budgets',
  'budgetApproval',
  'materials',
  'costEntries',
  'billingPlans',
  'timeAndMaterials',
  'timesheets',
  'boq',
  'progressClaims',
  'retention',
  'advances',
  'variations',
  'bonds',
  'revenueRecognition',
  'insights',
  'assistant',
] as const
export type ProjectFeature = (typeof PROJECT_FEATURES)[number]

export const PROJECT_RELEASES = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'LATER'] as const
export type ProjectRelease = (typeof PROJECT_RELEASES)[number]

export interface ProjectFeatureDef {
  en: string
  ar: string
  /** One sentence for Settings: what switching it on adds. */
  detailEn: string
  detailAr: string
  release: ProjectRelease
  /** Every one of these must be on too. */
  requires: readonly ProjectFeature[]
  /** At least one of these must be on too. */
  requiresAny: readonly ProjectFeature[]
}

export const PROJECT_FEATURE_DEFS: Record<ProjectFeature, ProjectFeatureDef> = {
  wbs: {
    en: 'Plan',
    ar: 'الخطة',
    detailEn: 'Phases, work packages and milestones on a timeline, with tasks placed in the plan.',
    detailAr: 'المراحل وحزم العمل والمعالم الرئيسية على مخطط زمني، مع وضع المهام في الخطة.',
    release: 'P1',
    requires: [],
    requiresAny: [],
  },
  budgets: {
    en: 'Budgets',
    ar: 'الموازنات',
    detailEn: 'Budget by cost category and phase, in versions, compared with actual cost.',
    detailAr: 'الموازنة حسب فئة التكلفة والمرحلة، بإصدارات متتالية، مقارنةً بالتكلفة الفعلية.',
    release: 'P2',
    requires: [],
    requiresAny: [],
  },
  budgetApproval: {
    en: 'Budget approval',
    ar: 'اعتماد الموازنة',
    detailEn: 'A budget becomes the baseline only after a second person or an approval workflow approves it.',
    detailAr: 'لا تصبح الموازنة معتمدة إلا بعد موافقة شخص آخر أو مسار اعتماد.',
    release: 'P2',
    requires: ['budgets'],
    requiresAny: [],
  },
  materials: {
    en: 'Materials from stock',
    ar: 'صرف المواد من المخزون',
    detailEn: 'Issue stock to a project and return it, at weighted-average cost.',
    detailAr: 'صرف المخزون للمشروع وإرجاعه بمتوسط التكلفة المرجح.',
    release: 'P2',
    requires: [],
    requiresAny: [],
  },
  costEntries: {
    en: 'Cost entries',
    ar: 'قيود التكلفة',
    detailEn: 'Allocate costs to a project, move costs between projects and record opening costs.',
    detailAr: 'تحميل التكاليف على المشروع ونقلها بين المشاريع وتسجيل التكاليف الافتتاحية.',
    release: 'P2',
    requires: [],
    requiresAny: [],
  },
  billingPlans: {
    en: 'Billing plans',
    ar: 'خطط الفوترة',
    detailEn: 'Milestone, percentage or fixed instalments invoiced through Sales in one click.',
    detailAr: 'دفعات حسب المعالم أو النسب أو المبالغ الثابتة تُفوتر عبر المبيعات بنقرة واحدة.',
    release: 'P3',
    requires: [],
    requiresAny: [],
  },
  timeAndMaterials: {
    en: 'Time and materials billing',
    ar: 'الفوترة بالوقت والمواد',
    detailEn: 'Bill approved hours and expenses at rates.',
    detailAr: 'فوترة الساعات والمصروفات المعتمدة حسب الأسعار.',
    release: 'P3',
    requires: [],
    requiresAny: [],
  },
  timesheets: {
    en: 'Time',
    ar: 'تسجيل الوقت',
    detailEn: 'Staff log hours on projects; managers approve them and the cost reaches the project.',
    detailAr: 'يسجل الموظفون ساعاتهم على المشاريع، ويعتمدها المديرون فتصل تكلفتها إلى المشروع.',
    release: 'P6',
    requires: [],
    requiresAny: [],
  },
  boq: {
    en: 'Bill of quantities',
    ar: 'جدول الكميات',
    detailEn: 'The priced list of contract items, imported from a file.',
    detailAr: 'قائمة بنود العقد المسعّرة، مع إمكانية استيرادها من ملف.',
    release: 'P4',
    requires: [],
    requiresAny: [],
  },
  progressClaims: {
    en: 'Progress claims',
    ar: 'المستخلصات',
    detailEn: 'Interim certificates from the bill of quantities, invoiced when the client certifies them.',
    detailAr: 'مستخلصات مرحلية من جدول الكميات تُفوتر عند اعتماد العميل لها.',
    release: 'P4',
    requires: ['boq'],
    requiresAny: [],
  },
  // Retention and advances run on progress claims (projects/04); on billing plans they come later.
  retention: {
    en: 'Retention',
    ar: 'المحتجزات',
    detailEn: 'The share the client holds back from each certificate, and its release.',
    detailAr: 'النسبة التي يحتجزها العميل من كل مستخلص، والإفراج عنها.',
    release: 'P4',
    requires: ['progressClaims'],
    requiresAny: [],
  },
  advances: {
    en: 'Advance payments',
    ar: 'الدفعات المقدمة',
    detailEn: 'An advance invoice at the start, recovered from later certificates.',
    detailAr: 'فاتورة دفعة مقدمة في البداية تُسترد من المستخلصات اللاحقة.',
    release: 'P4',
    requires: ['progressClaims'],
    requiresAny: [],
  },
  variations: {
    en: 'Variations',
    ar: 'الأوامر التغييرية',
    detailEn: 'Change orders that add, change or omit work and move the contract value and dates.',
    detailAr: 'أوامر تغيير تضيف الأعمال أو تعدلها أو تحذفها وتغير قيمة العقد وتواريخه.',
    release: 'P4',
    requires: ['boq'],
    requiresAny: [],
  },
  bonds: {
    en: 'Bonds and guarantees',
    ar: 'الضمانات البنكية',
    detailEn: 'Performance, advance and other guarantees with expiry reminders.',
    detailAr: 'ضمانات حسن التنفيذ والدفعة المقدمة وغيرها مع تذكير قبل انتهائها.',
    release: 'P5',
    requires: [],
    requiresAny: [],
  },
  revenueRecognition: {
    en: 'Revenue recognition',
    ar: 'إثبات الإيراد',
    detailEn: 'Month-end work-in-progress entries by percentage of completion.',
    detailAr: 'قيود أعمال قيد التنفيذ في نهاية الشهر حسب نسبة الإنجاز.',
    release: 'LATER',
    requires: ['budgets'],
    requiresAny: [],
  },
  insights: {
    en: 'Insights',
    ar: 'المؤشرات الذكية',
    detailEn: 'Health with its reasons, forecasts of final cost and finish, and alerts.',
    detailAr: 'حالة المشروع مع أسبابها، وتوقعات التكلفة النهائية وموعد الإنجاز، والتنبيهات.',
    release: 'P3',
    requires: [],
    requiresAny: [],
  },
  assistant: {
    en: 'AI assistant',
    ar: 'المساعد الذكي',
    detailEn: 'Ask about a project, draft status reports and get proposals you confirm.',
    detailAr: 'اسأل عن المشروع واكتب مسودات التقارير واحصل على اقتراحات تعتمدها بنفسك.',
    release: 'P8',
    requires: [],
    requiresAny: [],
  },
}

/** The functions built so far: Settings and project types show only these (the list grows release by release). */
export const PROJECT_FEATURES_AVAILABLE: readonly ProjectFeature[] = [
  'wbs',
  'budgets',
  'budgetApproval',
  'materials',
  'costEntries',
  'billingPlans',
  'timeAndMaterials',
  'timesheets',
  'boq',
  'variations',
  'progressClaims',
  'retention',
  'advances',
  'bonds',
  'insights',
  'assistant',
]

/** Each switch defaults per key, so a function added in a later release never resets the stored ones. */
export const projectFeaturesSchema = z.object({
  wbs: z.boolean().default(true),
  budgets: z.boolean().default(true),
  budgetApproval: z.boolean().default(false),
  materials: z.boolean().default(true),
  costEntries: z.boolean().default(true),
  billingPlans: z.boolean().default(true),
  timeAndMaterials: z.boolean().default(true),
  timesheets: z.boolean().default(true),
  boq: z.boolean().default(true),
  progressClaims: z.boolean().default(true),
  retention: z.boolean().default(true),
  advances: z.boolean().default(true),
  variations: z.boolean().default(true),
  bonds: z.boolean().default(true),
  revenueRecognition: z.boolean().default(false),
  insights: z.boolean().default(true),
  assistant: z.boolean().default(false),
})
export type ProjectFeatures = z.output<typeof projectFeaturesSchema>
export type ProjectFeaturesInput = z.input<typeof projectFeaturesSchema>

export const DEFAULT_PROJECT_FEATURES: ProjectFeatures = projectFeaturesSchema.parse({})

/** Switches that are on while a function they rely on is off (422 PROJECT_FEATURE_DEPENDENCY). */
export function projectFeatureDependencyErrors(
  features: ProjectFeatures,
): { feature: ProjectFeature; missing: readonly ProjectFeature[] }[] {
  const out: { feature: ProjectFeature; missing: readonly ProjectFeature[] }[] = []
  for (const feature of PROJECT_FEATURES) {
    if (!features[feature]) continue
    const def = PROJECT_FEATURE_DEFS[feature]
    const missing = def.requires.filter((r) => !features[r])
    if (missing.length) out.push({ feature, missing })
    else if (def.requiresAny.length && !def.requiresAny.some((r) => features[r])) {
      out.push({ feature, missing: def.requiresAny })
    }
  }
  return out
}

/** The effective switches: the company's AND the project's own copy. */
/**
 * Functions switched for the whole company only (P8 decision 21): a project follows the company switch,
 * and project types and projects offer no switch of their own (projects made before the function existed
 * carry no copy of it).
 */
export const PROJECT_FEATURES_COMPANY_ONLY: readonly ProjectFeature[] = ['assistant']

/** The functions a project or a project type may switch for itself. */
export const PROJECT_FEATURES_PER_PROJECT: readonly ProjectFeature[] = PROJECT_FEATURES_AVAILABLE.filter(
  (f) => !PROJECT_FEATURES_COMPANY_ONLY.includes(f),
)

export function effectiveProjectFeatures(
  company: ProjectFeatures,
  project: ProjectFeatures,
): ProjectFeatures {
  const out = { ...project }
  for (const f of PROJECT_FEATURES) {
    out[f] = company[f] && (PROJECT_FEATURES_COMPANY_ONLY.includes(f) || project[f])
  }
  return out
}

// ── settings (group `projects`) ──────────────────────────────────────────────

export const projectSettingsSchema = z.object({
  features: projectFeaturesSchema,
  /** The type preselected for a new project; null: none. */
  defaultTypeId: z.uuid().nullable(),
  /** The progress method of a project created without a type. */
  progressMethod: projectProgressMethodSchema,
  /** The cost category each source falls back to (null: the seeded one, PROJECT_COST_SOURCE_SEED_CODE). */
  categoryDefaults: projectCategoryDefaultsSchema.default(DEFAULT_PROJECT_CATEGORY_DEFAULTS),
  /** Markup on re-billed costs of time-and-materials projects, percent with two decimals (ASM-199). */
  tmExpenseMarkupPct: z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Invalid percentage')
    .default(DEFAULT_TM_EXPENSE_MARKUP_PCT),
  /** Contracting defaults, copied into a contract while it is a draft (04 §3.2). */
  contracting: contractingSettingsSchema.default(DEFAULT_CONTRACTING_SETTINGS),
  /** How a T&M invoice groups its lines (overridable per invoice). */
  tmGroupBy: z.enum(TM_GROUPINGS).default(DEFAULT_TM_GROUP_BY),
  /** Budget use (actual + committed ÷ budget, %) from which a project shows amber and notifies. */
  budgetWarnPct: z.number().int().min(1).max(500).default(DEFAULT_BUDGET_WARN_PCT),
  /** … and red. Must be above the warning level (`projectThresholdErrors`). */
  budgetCriticalPct: z.number().int().min(1).max(500).default(DEFAULT_BUDGET_CRITICAL_PCT),
  /** Time (P6): what may be logged when, and the week grid. */
  time: projectTimeSettingsSchema.default(DEFAULT_PROJECT_TIME_SETTINGS),
})

// ── project types and templates ──────────────────────────────────────────────

/** One plan node of a type's template; offsets and durations are working days from the project start. */
export const projectTemplateNodeSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9_.-]+$/, 'Letters, digits, dot, dash and underscore'),
  parentKey: z
    .string()
    .trim()
    .max(20)
    .nullish()
    .transform((v) => v || null),
  kind: wbsNodeKindSchema,
  name: requiredText(200),
  nameAr: optionalText(200),
  offsetDays: z.number().int().min(0).max(5000).default(0),
  durationDays: z.number().int().min(0).max(5000).default(0),
  weight: z.number().int().min(0).max(1000).default(1),
})
export type ProjectTemplateNode = z.output<typeof projectTemplateNodeSchema>

/** Parents come before their children, keys are unique and a milestone has no children. */
export function projectTemplateErrors(
  nodes: readonly ProjectTemplateNode[],
): { path: string; message: string }[] {
  const errors: { path: string; message: string }[] = []
  const seen = new Map<string, WbsNodeKind>()
  nodes.forEach((n, i) => {
    if (seen.has(n.key)) errors.push({ path: `template.nodes.${i}.key`, message: 'This key is used twice' })
    if (n.parentKey !== null) {
      const parentKind = seen.get(n.parentKey)
      if (parentKind === undefined) {
        errors.push({ path: `template.nodes.${i}.parentKey`, message: 'The parent must come earlier' })
      } else if (parentKind === 'MILESTONE') {
        errors.push({ path: `template.nodes.${i}.parentKey`, message: 'A milestone cannot have children' })
      }
    }
    seen.set(n.key, n.kind)
  })
  return errors
}

export const projectTemplateSchema = z
  .object({ nodes: z.array(projectTemplateNodeSchema).max(200).default([]) })
  .superRefine((t, ctx) => {
    for (const e of projectTemplateErrors(t.nodes)) {
      ctx.addIssue({ code: 'custom', path: e.path.split('.').slice(1), message: e.message })
    }
  })
export type ProjectTemplate = z.output<typeof projectTemplateSchema>
export type ProjectTemplateInput = z.input<typeof projectTemplateSchema>

const internalIsNonBillable = (v: { kind: ProjectKind; billingMethod: ProjectBillingMethod }) =>
  v.kind === 'CUSTOMER' || v.billingMethod === 'NON_BILLABLE'
const internalRule = { path: ['billingMethod'], message: 'An internal project is not billed' }

const projectTypeFields = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(20)
    .regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, dash and underscore'),
  name: requiredText(120),
  nameAr: optionalText(120),
  kind: projectKindSchema,
  billingMethod: projectBillingMethodSchema,
  features: projectFeaturesSchema,
  progressMethod: projectProgressMethodSchema,
  template: projectTemplateSchema,
})
export const projectTypeInputSchema = projectTypeFields.refine(internalIsNonBillable, internalRule)
export type ProjectTypeInput = z.input<typeof projectTypeInputSchema>
export const projectTypeUpdateSchema = projectTypeFields
  .extend({ version: versionSchema })
  .refine(internalIsNonBillable, internalRule)
export type ProjectTypeUpdate = z.input<typeof projectTypeUpdateSchema>
export const projectTypeListQuerySchema = z.object({ archived: z.stringbool().optional() })

export interface ProjectType {
  id: string
  code: string
  name: string
  nameAr: string | null
  kind: ProjectKind
  billingMethod: ProjectBillingMethod
  features: ProjectFeatures
  progressMethod: ProjectProgressMethod
  template: ProjectTemplate
  archived: boolean
  /** Projects created from it (any status) that the reader reaches under projects.read (0 without it). */
  projectCount: number
  version: number
}

export interface ProjectTypePreset {
  code: string
  name: string
  nameAr: string
  kind: ProjectKind
  billingMethod: ProjectBillingMethod
  /** Switches that differ from the defaults. */
  features: Partial<ProjectFeatures>
  progressMethod: ProjectProgressMethod
  template: ProjectTemplateInput
}

const phases = (...items: [string, string, string, number, number][]): ProjectTemplateInput => ({
  nodes: items.map(([key, name, nameAr, offsetDays, durationDays]) => ({
    key,
    parentKey: null,
    kind: 'PHASE' as const,
    name,
    nameAr,
    offsetDays,
    durationDays,
    weight: 1,
  })),
})

const CONTRACTING_OFF: Partial<ProjectFeatures> = {
  boq: false,
  progressClaims: false,
  retention: false,
  advances: false,
  variations: false,
  bonds: false,
}

/** The standard types a company adds in one click (Settings › Project types) and then edits. */
export const PROJECT_TYPE_PRESETS: readonly ProjectTypePreset[] = [
  {
    code: 'CONSTRUCTION',
    name: 'Construction contract',
    nameAr: 'عقد مقاولات',
    kind: 'CUSTOMER',
    billingMethod: 'PROGRESS_CLAIMS',
    features: { billingPlans: false, timeAndMaterials: false, timesheets: false },
    progressMethod: 'MILESTONES',
    template: phases(
      ['MOB', 'Mobilisation', 'التجهيز والتحشيد', 0, 15],
      ['SUB', 'Substructure', 'الأعمال تحت الأرض', 10, 60],
      ['SUP', 'Superstructure', 'الهيكل الإنشائي', 60, 120],
      ['FIN', 'Finishes', 'التشطيبات', 150, 90],
      ['MEP', 'MEP', 'الأعمال الكهروميكانيكية', 120, 120],
      ['HND', 'Handover', 'التسليم', 240, 15],
    ),
  },
  {
    code: 'FITOUT',
    name: 'Fit-out / MEP contract',
    nameAr: 'عقد تجهيزات داخلية / أعمال كهروميكانيكية',
    kind: 'CUSTOMER',
    billingMethod: 'PROGRESS_CLAIMS',
    features: { bonds: false, timeAndMaterials: false, timesheets: false },
    progressMethod: 'MILESTONES',
    template: phases(
      ['DES', 'Design', 'التصميم', 0, 20],
      ['PRC', 'Procurement', 'التوريد', 15, 40],
      ['INS', 'Installation', 'التركيب', 40, 60],
      ['TST', 'Testing', 'الاختبار', 95, 10],
      ['HND', 'Handover', 'التسليم', 105, 5],
    ),
  },
  {
    code: 'SERVICE_FIXED',
    name: 'Fixed-price service',
    nameAr: 'خدمة بسعر مقطوع',
    kind: 'CUSTOMER',
    billingMethod: 'FIXED_PRICE',
    features: { ...CONTRACTING_OFF, timeAndMaterials: false },
    progressMethod: 'MILESTONES',
    template: phases(
      ['DIS', 'Discovery', 'الاستكشاف', 0, 10],
      ['DSN', 'Design', 'التصميم', 10, 15],
      ['BLD', 'Build', 'التنفيذ', 25, 40],
      ['TST', 'Test', 'الاختبار', 65, 10],
      ['GOL', 'Go-live', 'التشغيل', 75, 5],
    ),
  },
  {
    code: 'TIME_MATERIALS',
    name: 'Time and materials',
    nameAr: 'الوقت والمواد',
    kind: 'CUSTOMER',
    billingMethod: 'TIME_AND_MATERIALS',
    features: { ...CONTRACTING_OFF, billingPlans: false },
    progressMethod: 'TASKS',
    template: phases(['WRK', 'Work', 'العمل', 0, 60]),
  },
  {
    code: 'INTERNAL',
    name: 'Internal project',
    nameAr: 'مشروع داخلي',
    kind: 'INTERNAL',
    billingMethod: 'NON_BILLABLE',
    features: {
      ...CONTRACTING_OFF,
      billingPlans: false,
      timeAndMaterials: false,
      budgetApproval: true,
    },
    progressMethod: 'MILESTONES',
    template: phases(
      ['PLN', 'Plan', 'التخطيط', 0, 10],
      ['EXE', 'Execute', 'التنفيذ', 10, 60],
      ['CLS', 'Close', 'الإقفال', 70, 5],
    ),
  },
]

// ── projects ─────────────────────────────────────────────────────────────────

const projectFields = z.object({
  name: requiredText(200),
  nameAr: optionalText(200),
  description: optionalText(4000),
  priority: projectPrioritySchema.default('NORMAL'),
  /** Required for a CUSTOMER project; must be null for an INTERNAL one. */
  customerId: nullableUuid,
  /** The site: a work location (governorate, wilayat, address, geo). */
  workLocationId: nullableUuid,
  startDate: z.iso.date(),
  endDate: nullableDate,
  /** The original contract value, before variations; null when not agreed (T&M, internal). */
  contractValue: moneySchema
    .refine((v) => Number(v) >= 0, 'Cannot be negative')
    .nullish()
    .transform((v) => v ?? null),
  /** The client's contract or purchase-order reference. */
  contractReference: optionalText(100),
  /** Output VAT of the project's billing (items, T&M lines); null: the company's default rate. */
  taxRateId: nullableUuid,
  /**
   * The customer was quoted VAT-inclusive (copied from the quotation). Display only: Projects invoices
   * are always ex-VAT (03 §2 rule 2).
   */
  pricesIncludeTax: z.boolean().default(false),
})

const endAfterStart = (v: { startDate: string; endDate: string | null }) =>
  v.endDate === null || v.endDate >= v.startDate
const endRule = { path: ['endDate'], message: 'The end is before the start' }

/**
 * `POST /projects`. Omitted fields come from the type (kind, billing method, progress method,
 * functions, template) or the company's settings. `orgUnitId` omitted: the creator's home unit;
 * explicit null: none. `managerId` omitted: the creator.
 */
export const projectCreateSchema = projectFields
  .extend({
    typeId: nullableUuid,
    kind: projectKindSchema.optional(),
    billingMethod: projectBillingMethodSchema.optional(),
    progressMethod: projectProgressMethodSchema.optional(),
    managerId: z.uuid().optional(),
    orgUnitId: z.uuid().nullable().optional(),
    /** Seed the plan from the type's template (false: start with an empty plan). */
    applyTemplate: z.boolean().default(true),
  })
  .refine(endAfterStart, endRule)
export type ProjectCreateInput = z.input<typeof projectCreateSchema>

/** `PATCH /projects/:id`. The kind and the type never change; the billing method only while PLANNING. */
export const projectUpdateSchema = projectFields
  .extend({
    /**
     * Omitted: unchanged (users without projects.financials never see it, so they never send it).
     * A value or null changes it and needs projects.financials (403 otherwise).
     */
    contractValue: moneySchema
      .refine((v) => Number(v) >= 0, 'Cannot be negative')
      .nullable()
      .optional(),
    /**
     * Omitted: unchanged (a default would silently reset them when an older client leaves them out); a
     * value (or null for the rate) changes it.
     */
    taxRateId: z.uuid().nullable().optional(),
    pricesIncludeTax: z.boolean().optional(),
    /** Required on edit: a default would silently reset it when a client leaves it out. */
    priority: projectPrioritySchema,
    billingMethod: projectBillingMethodSchema,
    progressMethod: projectProgressMethodSchema,
    managerId: z.uuid(),
    orgUnitId: nullableUuid,
    version: versionSchema,
  })
  .refine(endAfterStart, endRule)
export type ProjectUpdateInput = z.input<typeof projectUpdateSchema>

/** A project's own copy of its functions (within what the company has on). */
export const projectFeaturesUpdateSchema = z.object({
  features: projectFeaturesSchema,
  version: versionSchema,
})
export type ProjectFeaturesUpdate = z.input<typeof projectFeaturesUpdateSchema>

/**
 * Portfolio views (each a list filter with its count on its tab).
 *   mine       projects I manage or am a member of (not CANCELLED or CLOSED)
 *   active     PLANNING, ACTIVE, ON_HOLD
 *   attention  open projects whose stored health has a warning or critical signal (P5); projects not
 *              yet measured fall back to P1's rule (an overdue milestone, past the end date)
 *   all        every status
 *   closed     COMPLETED, CLOSED, CANCELLED
 */
export const PROJECT_VIEWS = ['mine', 'active', 'attention', 'all', 'closed'] as const
export type ProjectView = (typeof PROJECT_VIEWS)[number]
export const PROJECT_SORT_FIELDS = [
  'code',
  'name',
  'customer',
  'manager',
  'status',
  'priority',
  'startDate',
  'endDate',
  'progress',
  'updatedAt',
  /**
   * P5: worst stored health first ascending (OFF_TRACK, AT_RISK, ON_TRACK, NOT_MEASURED, then projects not
   * yet measured), then the most warning or critical signals. The attention view's default.
   */
  'health',
] as const

const projectFilterFields = {
  status: projectStatusSchema.optional(),
  kind: projectKindSchema.optional(),
  typeId: z.uuid().optional(),
  managerId: z.uuid().optional(),
  customerId: z.uuid().optional(),
  orgUnitId: z.uuid().optional(),
  priority: projectPrioritySchema.optional(),
  /**
   * The stored health (P5, 05 §7.2): projects whose last measurement is this level, or carries this
   * signal. A money or billing signal the viewer may not see matches nothing.
   */
  health: z.enum(HEALTH_LEVELS).optional(),
  signal: z.enum(SIGNAL_CODES).optional(),
}
export const projectListQuerySchema = pageQuerySchema.extend({
  view: z.enum(PROJECT_VIEWS).default('mine'),
  ...projectFilterFields,
})
export type ProjectListQuery = z.input<typeof projectListQuerySchema>
export const projectSummaryQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  ...projectFilterFields,
})
export type ProjectSummaryQuery = z.input<typeof projectSummaryQuerySchema>
/** `GET /projects/timeline`: bars and milestones of up to 200 projects overlapping [from, to]. */
export const projectTimelineQuerySchema = projectSummaryQuerySchema.extend({
  view: z.enum(PROJECT_VIEWS).default('active'),
  from: z.iso.date(),
  to: z.iso.date(),
})
export type ProjectTimelineQuery = z.input<typeof projectTimelineQuerySchema>
/** `GET /projects/options` for pickers: open projects by code or name (EN/AR), at most 20. */
export const projectOptionsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  customerId: z.uuid().optional(),
  /** Include the project with this id even if it no longer matches (a picker's kept value). */
  include: z.uuid().optional(),
  /**
   * COST (default): projects a cost may be tagged to. REVENUE: CUSTOMER projects, of `customerId` when
   * given (sales documents, 02 §3.6).
   */
  use: z.enum(['COST', 'REVENUE']).default('COST'),
})
export type ProjectOptionsQuery = z.input<typeof projectOptionsQuerySchema>
/** The most rows one export returns; more is refused (narrow the filters). */
export const PROJECT_EXPORT_MAX_ROWS = 20_000
/** `GET /projects/export`: the list's view, filters and sort as CSV; `contractValue` only with financials. */
export const projectExportQuerySchema = projectSummaryQuerySchema.extend({
  view: z.enum(PROJECT_VIEWS).default('mine'),
  sort: pageQuerySchema.shape.sort,
  lang: z.enum(['en', 'ar']).default('en'),
})
export type ProjectExportQuery = z.input<typeof projectExportQuerySchema>

export interface ProjectUserRef {
  id: string
  name: string
  nameAr: string | null
  active: boolean
}
export interface ProjectCustomerRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}
export interface ProjectTypeRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}
export interface ProjectUnitRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}
export interface ProjectLocationRef {
  id: string
  code: string
  name: string
  nameAr: string | null
  /** OMAN_GOVERNORATES / wilayat codes (contracts oman-places.ts). */
  governorate: string | null
  wilayat: string | null
}

export interface ProjectMilestoneRef {
  id: string
  name: string
  nameAr: string | null
  /** Planned finish date. */
  date: string | null
  achievedAt: string | null
}

export interface ProjectListItem {
  id: string
  code: string
  name: string
  nameAr: string | null
  kind: ProjectKind
  billingMethod: ProjectBillingMethod
  status: ProjectStatus
  priority: ProjectPriority
  type: ProjectTypeRef | null
  customer: ProjectCustomerRef | null
  manager: ProjectUserRef
  orgUnit: ProjectUnitRef | null
  startDate: string
  endDate: string | null
  /** 0–100 by the project's progress method. */
  progress: string
  progressMethod: ProjectProgressMethod
  nextMilestone: ProjectMilestoneRef | null
  overdueMilestones: number
  /** Open and past its end date. */
  pastEnd: boolean
  /** Null when the viewer may not see money (projects.financials). */
  contractValue: string | null
  memberCount: number
  /** The stored health (P5); null before the first run. Optional in the type only for older fixtures. */
  health?: StoredProjectHealth | null
  updatedAt: string
  version: number
}

export interface ProjectMember {
  user: ProjectUserRef
  accessRole: ProjectMemberRole
  /** Free text ("Site engineer"); legacy role names land here. */
  roleTitle: string | null
  /** P6: the rate role whose bill rate their billable time takes (unless `billRate` is set). */
  rateRole?: RateRoleRef | null
  /** P6: the member's own bill rate on this project, per hour; only with projects.financials. */
  billRate?: string | null
  /** P6: planned share of their working week on this project, 1–100 (expected hours, utilisation). */
  allocationPct?: number | null
  addedAt: string
  addedBy: ProjectUserRef | null
}

export interface ProjectCapabilities {
  edit: boolean
  editFeatures: boolean
  manageMembers: boolean
  editPlan: boolean
  updateProgress: boolean
  activate: boolean
  hold: boolean
  resume: boolean
  complete: boolean
  close: boolean
  reopen: boolean
  cancel: boolean
  addNote: boolean
  pinNotes: boolean
  attach: boolean
  viewFinancials: boolean
  /** Prepare, submit and withdraw budget versions (projects.budgets, the `budgets` function on). */
  editBudget: boolean
  /** Approve a PENDING budget (projects.budgets_approve), unless they prepared or submitted it. */
  approveBudget: boolean
  /** Issue stock to the project and take it back (projects.materials, the `materials` function on, open). */
  issueMaterials: boolean
  /** Record cost entries on it (projects.cost_entries, the `costEntries` function on, open). */
  addCostEntries: boolean
  /** Add, edit and cancel billing plan items (manage* and projects.financials, `billingPlans` on, open). */
  editBillingPlan: boolean
  /** Preview and issue invoices, overrides, void Projects documents (projects.bill, CUSTOMER, open, Sales on). */
  bill: boolean
}

/** Why a lifecycle command is not offered although the viewer may act on the project. */
export type ProjectBlockReason =
  | 'NOT_PERMITTED'
  | 'ILLEGAL_MOVE'
  | 'NO_START_DATE'
  | 'OPEN_MILESTONES'
  | 'FEATURE_OFF'
  /** Close: a budget waits for approval or draft purchases are still tagged to it (PROJECT_CLOSE_BLOCKED). */
  | 'PENDING_WORK'

export interface ProjectDetail extends ProjectListItem {
  description: string | null
  contractReference: string | null
  workLocation: ProjectLocationRef | null
  /** The quotation this project started from (03 §7). */
  quotation: { id: string; number: string } | null
  taxRate: { id: string; name: string; nameAr: string | null; rate: string } | null
  pricesIncludeTax: boolean
  /** The project's own copy of its functions. */
  ownFeatures: ProjectFeatures
  /** What actually applies: the company's switches AND the project's own. */
  features: ProjectFeatures
  actualStart: string | null
  actualFinish: string | null
  statusReason: string | null
  statusChangedAt: string | null
  createdBy: ProjectUserRef
  createdAt: string
  members: ProjectMember[]
  capabilities: ProjectCapabilities
  blocked: Partial<Record<ProjectCommand, ProjectBlockReason>>
}

export type ProjectViewCounts = Record<ProjectView, number>
export interface ProjectSummary {
  counts: ProjectViewCounts
  /** Sum of the original contract values of the `active` view; null without projects.financials. */
  activeContractValue: string | null
  /**
   * The portfolio strip (06 §5.1, P5) over the `active` view; each null without projects.financials.
   * Optional in the type only for older fixtures.
   */
  strip?: {
    billed: string | null
    costToDate: string | null
    /** Σ forecast margin ÷ Σ revenue at completion of measured projects, one decimal. */
    forecastMarginPct: string | null
    unbilled: string | null
    retentionHeld: string | null
    /** When the stored health was last computed (oldest of the projects shown). */
    healthAsOf: string | null
  }
}

export interface ProjectTimelineItem {
  id: string
  code: string
  name: string
  nameAr: string | null
  status: ProjectStatus
  customer: ProjectCustomerRef | null
  manager: ProjectUserRef
  startDate: string
  endDate: string | null
  progress: string
  milestones: ProjectMilestoneRef[]
  /** The stored health (P5), redacted as on the list; null before the first run. Optional for older fixtures. */
  health?: StoredProjectHealth | null
}

export interface ProjectOption {
  id: string
  code: string
  name: string
  nameAr: string | null
  status: ProjectStatus
  customerId: string | null
}

// ── members ──────────────────────────────────────────────────────────────────

/**
 * `GET /projects/people`: the manager and member picker (active users by name, EN/AR, at most 20), for
 * holders of projects.create or projects.manage and the project's managers. Names only: no e-mail, phone
 * or roles.
 */
export const projectPeopleQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  /** Include this user even if inactive (a picker's kept value). */
  include: z.uuid().optional(),
})
export type ProjectPeopleQuery = z.input<typeof projectPeopleQuerySchema>

/**
 * `PUT /projects/:id/members/:userId`: add a member or change their role. P6: the rate role, the member's
 * own bill rate (needs projects.financials) and the allocation: each omitted keeps its value, null clears
 * it (P6 decision 31).
 */
export const projectMemberInputSchema = z.object({
  accessRole: projectMemberRoleSchema.default('MEMBER'),
  roleTitle: optionalText(120),
  projectRoleId: z.uuid().nullable().optional(),
  billRate: moneySchema
    .refine((v) => Number(v) >= 0, 'Cannot be negative')
    .nullable()
    .optional(),
  allocationPct: z.number().int().min(1).max(100).nullable().optional(),
})
export type ProjectMemberInput = z.input<typeof projectMemberInputSchema>

// ── plan (WBS) ───────────────────────────────────────────────────────────────

const nodeFields = z.object({
  kind: wbsNodeKindSchema,
  name: requiredText(200),
  nameAr: optionalText(200),
  /** A milestone has only a finish date (its due date). */
  plannedStart: nullableDate,
  plannedFinish: nullableDate,
  weight: z.number().int().min(0).max(1000).default(1),
})
const nodeDatesOk = (v: { kind: WbsNodeKind; plannedStart: string | null; plannedFinish: string | null }) =>
  v.kind === 'MILESTONE'
    ? v.plannedStart === null
    : v.plannedStart === null || v.plannedFinish === null || v.plannedFinish >= v.plannedStart
const nodeDatesRule = { path: ['plannedFinish'], message: 'The finish is before the start' }

/** `POST /projects/:id/wbs/nodes`: added as the last child of `parentId` (null: a top-level node). */
export const wbsNodeCreateSchema = nodeFields
  .extend({ parentId: nullableUuid })
  .refine(nodeDatesOk, nodeDatesRule)
export type WbsNodeCreateInput = z.input<typeof wbsNodeCreateSchema>
/** `PATCH /projects/:id/wbs/nodes/:nodeId`. A node with children cannot become a milestone. */
export const wbsNodeUpdateSchema = nodeFields
  .extend({ version: versionSchema })
  .refine(nodeDatesOk, nodeDatesRule)
export type WbsNodeUpdateInput = z.input<typeof wbsNodeUpdateSchema>
/** `POST …/move`: under `parentId` at `position` (0-based among its siblings). */
export const wbsNodeMoveSchema = z.object({
  parentId: nullableUuid,
  position: z.number().int().min(0).max(WBS_NODE_LIMIT),
  version: versionSchema,
})
export type WbsNodeMoveInput = z.input<typeof wbsNodeMoveSchema>
/** `POST …/achieve` (milestones): the date the client accepted or the milestone was met. */
export const wbsNodeAchieveSchema = z.object({
  achievedOn: z.iso.date(),
  note: optionalText(1000),
  version: versionSchema,
})
export type WbsNodeAchieveInput = z.input<typeof wbsNodeAchieveSchema>
/** `POST …/unachieve`: takes a milestone back, with a reason. */
export const wbsNodeUnachieveSchema = projectReasonCommandSchema
/** `POST …/progress` (MANUAL method): an append-only dated update. */
export const wbsProgressUpdateSchema = z.object({
  percent: percentSchema,
  asOf: z.iso.date(),
  note: optionalText(1000),
})
export type WbsProgressUpdateInput = z.input<typeof wbsProgressUpdateSchema>
/** `POST /projects/:id/wbs/apply-template`: only on an empty plan. */
export const wbsApplyTemplateSchema = z.object({ typeId: nullableUuid, version: versionSchema })
export type WbsApplyTemplateInput = z.input<typeof wbsApplyTemplateSchema>
/** `PUT /projects/:id/tasks/:taskId/node`: where a project task sits in the plan (null: nowhere). */
export const projectTaskNodeSchema = z.object({ nodeId: nullableUuid })
export type ProjectTaskNodeInput = z.input<typeof projectTaskNodeSchema>

export interface WbsNode {
  id: string
  parentId: string | null
  kind: WbsNodeKind
  /** Outline code ("2.3"), recomputed when nodes move. */
  outline: string
  position: number
  name: string
  nameAr: string | null
  plannedStart: string | null
  plannedFinish: string | null
  weight: number
  /** 0–100 by the project's progress method. */
  progress: string
  achievedAt: string | null
  /** Open tasks placed in this node and its descendants that the viewer can see. */
  openTasks: number
  overdue: boolean
  version: number
}

export interface WbsProgressEntry {
  id: string
  nodeId: string
  percent: string
  asOf: string
  note: string | null
  by: ProjectUserRef
  createdAt: string
}

export interface ProjectPlan {
  projectVersion: number
  nodes: WbsNode[]
  /** The latest updates (MANUAL method), newest first, at most 50. */
  recentProgress: WbsProgressEntry[]
  /** Where the project's tasks sit in the plan: only tasks the viewer can see, only placed ones. */
  taskPlacements: { taskId: string; nodeId: string }[]
}

// ── notes ────────────────────────────────────────────────────────────────────

export const projectNoteInputSchema = z.object({ body: requiredText(4000) })
export type ProjectNoteInput = z.input<typeof projectNoteInputSchema>

export interface ProjectNote {
  id: string
  body: string
  author: ProjectUserRef
  createdAt: string
  pinnedAt: string | null
  pinnedBy: ProjectUserRef | null
  /** The viewer may pin, unpin or remove it. */
  canModerate: boolean
}

// ── activity ─────────────────────────────────────────────────────────────────

export const projectActivityQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})

export interface ProjectActivityItem {
  id: string
  at: string
  actor: ProjectUserRef | null
  /** The audit action (CREATE, UPDATE, ACTIVATE, …) and what it touched. */
  action: string
  entity: 'PROJECT' | 'MEMBER' | 'NODE' | 'NOTE' | 'FILE'
  /** Short, already-safe facts for the line ("status": "ACTIVE", "node": "2.3 Finishes"). */
  details: Record<string, string | number | boolean | null>
}

// ── errors ───────────────────────────────────────────────────────────────────

/** Problem codes the projects API answers with (besides the common ones). */
export const PROJECT_ERROR_CODES = [
  /** 422: a lifecycle command from a status it does not leave from (like DSR_ILLEGAL_TRANSITION). */
  'PROJECT_ILLEGAL_MOVE',
  /**
   * 422: closing while work is pending, with `blockers: { code, count }[]`, codes PENDING_BUDGET (a
   * budget waits for approval), OPEN_COMMITMENTS (draft purchases are tagged to it) and
   * OPEN_BILLING_ITEMS (billing plan items neither invoiced nor cancelled); P6 adds SUBMITTED_TIME (time
   * waiting for approval); contracts add open claims.
   */
  'PROJECT_CLOSE_BLOCKED',
  /** 422: CLOSED or CANCELLED projects take no documents and no work. */
  'PROJECT_NOT_OPEN',
  /** 409: the function is off for the company or the project ({ feature, level }). */
  'PROJECT_FEATURE_OFF',
  /** 422: a switch is on while a function it relies on is off. */
  'PROJECT_FEATURE_DEPENDENCY',
  /** 409: a new or changed project tag while the module is off. */
  'PROJECTS_MODULE_OFF',
  /** 422: a CUSTOMER project needs a customer; an INTERNAL one has none. */
  'PROJECT_CUSTOMER_REQUIRED',
  /** 422: the document's customer is not the project's. */
  'PROJECT_CUSTOMER_MISMATCH',
  /** 422: the manager must be an active user. */
  'PROJECT_MANAGER_INACTIVE',
  /** 422: the manager cannot be removed from the members, or made a plain member. */
  'PROJECT_MANAGER_MEMBERSHIP',
  /** 422: a project type that is archived cannot start new projects. */
  'PROJECT_TYPE_ARCHIVED',
  /** 409: another project type has this code. */
  'PROJECT_TYPE_CODE_TAKEN',
  /** 422: the progress method is not available yet or needs a function that is off. */
  'PROJECT_PROGRESS_METHOD_UNAVAILABLE',
  /** 422: the plan would loop (a node under its own descendant). */
  'PROJECT_NODE_CYCLE',
  /** 422: a milestone cannot have children. */
  'PROJECT_MILESTONE_LEAF',
  /** 422: more than WBS_NODE_LIMIT nodes. */
  'PROJECT_NODE_LIMIT',
  /** 404/422: the node is not in this project. */
  'PROJECT_NODE_NOT_IN_PROJECT',
  /** 422: only milestones are achieved. */
  'PROJECT_NOT_A_MILESTONE',
  /** 409: a template applies only to an empty plan. */
  'PROJECT_PLAN_NOT_EMPTY',
  /** 422: the task is not linked to this project. */
  'PROJECT_TASK_NOT_LINKED',
  /** 422: a task links to one project at most. */
  'PROJECT_TASK_ONE_PROJECT',
  /** 422: completing needs every milestone achieved (or removed). */
  'PROJECT_OPEN_MILESTONES',
] as const
export type ProjectErrorCode = (typeof PROJECT_ERROR_CODES)[number]

// ── plan apply (P8; usable without the assistant) ───────────────────────────

/**
 * `POST /projects/:id/plan/apply` (projects.manage; tasks need tasks.create): appends phases with their
 * milestones and tasks to the plan in one transaction, dates already worked out by the client from the
 * project's calendar. At most 20 phases and 200 tasks (422 PLAN_TOO_LARGE). `aiRunId` records that the
 * assistant proposed it (projects/05 §16.4).
 */
export const planApplySchema = z
  .object({
    aiRunId: z.uuid().optional(),
    phases: z
      .array(
        z.object({
          name: requiredText(200),
          nameAr: optionalText(200),
          plannedStart: z.iso.date(),
          plannedFinish: z.iso.date(),
          weight: z
            .number()
            .min(0)
            .max(100)
            .nullish()
            .transform((v) => v ?? null),
          milestones: z
            .array(
              z.object({ name: requiredText(200), nameAr: optionalText(200), plannedFinish: z.iso.date() }),
            )
            .max(10)
            .default([]),
          tasks: z
            .array(
              z.object({
                title: requiredText(200),
                dueDate: z.iso
                  .date()
                  .nullish()
                  .transform((v) => v ?? null),
              }),
            )
            .max(200)
            .default([]),
        }),
      )
      .min(1, 'Add at least one phase')
      .max(20),
  })
  .refine((v) => v.phases.every((p) => p.plannedFinish >= p.plannedStart), {
    path: ['phases'],
    message: 'A phase finishes before it starts',
  })
export type PlanApplyInput = z.input<typeof planApplySchema>
/** At most this many tasks across the phases of one apply. */
export const PLAN_APPLY_MAX_TASKS = 200
